import { useQuery } from 'react-query';

import type {
  LoadFileImportByteProgress,
  LoadFileImportCounters,
} from '../../../../backend/src/integrations/relativity/load-file-import/load-file-import-types';
import { QueryEntity } from '../types';
import { APIBaseChronos } from '@/api/hosts';
import { useFetcher } from '@/api/useGetFetchConfig';

export type LoadFileImportProgressStatus = 'pending' | 'processing' | 'complete' | 'failed';

interface LoadFileImportStatusResponse extends LoadFileImportCounters {
  status: LoadFileImportProgressStatus;
  bytesRead?: number;
  totalBytes?: number;
}

interface UseGetLoadFileImportStatusReturn {
  counters: LoadFileImportCounters | null;
  byteProgress: LoadFileImportByteProgress | null;
  isComplete: boolean;
  error: string | null;
}

const POLL_INTERVAL_MS = 1000;

const isTerminal = (status: LoadFileImportProgressStatus | undefined) => status === 'complete' || status === 'failed';

/**
 * Polls the load file import status endpoint once a second while an import runs,
 * stopping once the job reaches a terminal state. Pass the `importId` (the status
 * route is keyed by import, not job id) once `/start` has been called.
 */
const useGetLoadFileImportStatus = (
  matterId: string | undefined,
  importId: string | undefined,
  options: { enabled?: boolean } = {},
): UseGetLoadFileImportStatusReturn => {
  const { enabled = true } = options;
  const fetcher = useFetcher();
  const isEnabled = enabled && !!matterId && !!importId;

  const { data, error } = useQuery<LoadFileImportStatusResponse, Error>(
    [QueryEntity.LoadFileImportStatus, { matterId, importId }],
    () => fetcher(`${APIBaseChronos}/api/relativity/load-file-import/${importId}/status?matterId=${matterId}`),
    {
      enabled: isEnabled,
      refetchOnWindowFocus: false,
      refetchInterval: (latest) => (isTerminal(latest?.status) ? false : POLL_INTERVAL_MS),
    },
  );

  const { status, bytesRead, totalBytes, ...counters } = data ?? {};
  const isComplete = status === 'complete';

  return {
    counters: data ? (counters as LoadFileImportCounters) : null,
    byteProgress: bytesRead !== undefined && totalBytes !== undefined ? { bytesRead, totalBytes } : null,
    isComplete,
    error: status === 'failed' ? 'Import failed' : error ? 'Failed to fetch import progress' : null,
  };
};

export default useGetLoadFileImportStatus;
